import { useCallback } from "react";

import { BTInput } from "@btnet/core-components";

import { BdsButton, BdsLayout, BdsSection } from "@buildertrend/components";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconDeleteFilled } from "commonComponents/btWrappers/BTIcon";
import {
    Email,
    type IInputEmailProps,
    type InputEmailFormValue,
} from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { convertColLayoutToProps } from "commonComponents/entity/email/InputEmail/InputEmail.utils";

/**
 * Standard email input component, includes email address field, friendly label field, and
 * optional delete button
 */
export const InputEmail: React.FC<IInputEmailProps> = ({
    id,
    "data-testid": dataTestId,
    isReadOnly = false,
    canDelete = false,
    emailInputLabel,
    labelInputLabel,
    columnLayout = "auto",
    value,
    onChange,
    onBlur,
    onDelete,
    emailFieldRef,
}) => {
    const emailAddressKey: keyof InputEmailFormValue = "emailAddress";
    const friendlyNameKey: keyof InputEmailFormValue = "friendlyName";

    // Sanitize id for HTML attributes (replace [ and ] with _)
    // Original id format (e.g., "additionalEmail[0]") is preserved for callbacks
    const sanitizeHtmlId = (rawId: string) => rawId.replace(/[[\]]/g, "_");
    const sanitizedId = sanitizeHtmlId(id);

    const emailAddressId = [id, emailAddressKey].join(".");
    const sanitizedEmailAddressId = sanitizeHtmlId(emailAddressId);
    const friendlyNameId = [id, friendlyNameKey].join(".");
    const sanitizedFriendlyNameId = sanitizeHtmlId(friendlyNameId);

    const [emailColProps, labelColProps] = convertColLayoutToProps(columnLayout);

    const handleChange = useCallback(
        (fieldId: string, fieldValue: InputEmailFormValue | string) => {
            const originalFieldId = fieldId.replace(sanitizedId, id);
            onChange(originalFieldId, fieldValue);
        },
        [id, sanitizedId, onChange],
    );

    const handleBlur = useCallback(
        (fieldId: string, touched?: boolean, value?: string | number) => {
            if (onBlur) {
                const originalFieldId = fieldId.replace(sanitizedId, id);
                onBlur(originalFieldId, touched, value);
            }
        },
        [id, sanitizedId, onBlur],
    );

    const handleDelete = useCallback(
        () => (onDelete ? onDelete(id) : onChange(id, new Email())),
        [id, onChange, onDelete],
    );

    const emailInput = (
        <BTFormItemAutomatic id={emailAddressId} label={emailInputLabel} useBds>
            <BTInput<undefined>
                fieldRef={emailFieldRef}
                id={sanitizedEmailAddressId}
                data-testid={`${dataTestId}-emailAddress`}
                readOnly={isReadOnly}
                value={value.emailAddress}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </BTFormItemAutomatic>
    );

    const labelInput = (
        <BTFormItemAutomatic id={friendlyNameId} label={labelInputLabel} useBds>
            <BTInput<undefined>
                id={sanitizedFriendlyNameId}
                data-testid={`${dataTestId}-friendlyName`}
                readOnly={isReadOnly}
                value={value.friendlyName}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </BTFormItemAutomatic>
    );

    return (
        <BdsLayout displayType="1and1" className="bds-py-2">
            <BdsSection data-testid={`${dataTestId}-emailAddressCol`} {...emailColProps}>
                {emailInput}
            </BdsSection>
            <BdsSection data-testid={`${dataTestId}-friendlyNameCol`} {...labelColProps}>
                {canDelete ? (
                    <div className="flex flex-row gap-1 align-bottom">
                        <div className="flex flex-col grow">{labelInput}</div>
                        <div className="flex flex-col padding-top-lg">
                            <BdsButton
                                testid={`${dataTestId}-delete`}
                                icon={<BTIconDeleteFilled />}
                                onClick={handleDelete}
                                displayType="tertiary"
                                ariaLabel="Delete"
                            />
                        </div>
                    </div>
                ) : (
                    labelInput
                )}
            </BdsSection>
        </BdsLayout>
    );
};
